<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, watch, inject, computed } from "vue"
import type { Address } from "@types"
import type { Services } from "@services"
import type { AutoCompleteCompleteEvent } from "primevue/autocomplete"
import type {
    CompanySearchResult,
    CompanyInseeResult,
} from "@core/services/company.service"

// ================================
// TYPES & INTERFACES
// ================================
export interface CompanyFormValue {
    _id?: string
    name: string
    type?: string
    address?: Address | null
    idcompany?: string
    legalForm?: string
}

interface Suggestion {
    value: string
    name: string
    company: CompanyFormValue
}

interface SuggestionGroup {
    label: string
    items: Suggestion[]
}

interface Props {
    /**
     * Valeur de l'entreprise (v-model)
     */
    modelValue?: CompanyFormValue | null
    /**
     * Type d'entreprise à rechercher
     */
    companyType?: string
    /**
     * Identifiant du champ
     */
    inputId?: string
    /**
     * Label du champ
     */
    label?: string
    /**
     * Message d'aide
     */
    hint?: string
    /**
     * Message d'erreur
     */
    error?: string
    /**
     * Champ obligatoire
     */
    required?: boolean
    /**
     * Champ désactivé
     */
    disabled?: boolean
    /**
     * Champ en lecture seule
     */
    readonly?: boolean
    /**
     * Désactive la recherche (saisie manuelle uniquement)
     */
    disableSearch?: boolean
}

interface Emits {
    /**
     * Émis lors du changement de valeur
     */
    (e: "update:modelValue", value: CompanyFormValue | null): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    companyType: "entreprise",
    inputId: "input-company",
    label: "",
    hint: "",
    error: "",
    required: false,
    disabled: false,
    readonly: false,
    disableSearch: false,
})

const emit = defineEmits<Emits>()

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")

// ================================
// ÉTAT
// ================================

/**
 * Valeur sélectionnée dans l'autocomplete
 */
const selectedValue = ref<Suggestion | string | null>(null)

/**
 * Valeur du champ texte en mode manuel
 */
const manualValue = ref<string>("")

/**
 * Liste des suggestions groupées
 */
const suggestions = ref<SuggestionGroup[]>([])

/**
 * Indique si la recherche est en cours
 */
const isLoading = ref(false)

/**
 * AbortController pour annuler les requêtes
 */
const controller = ref<AbortController | null>(null)

/**
 * Affiche le bouton effacer (mode recherche sans équivalent PrimeVue intégré)
 */
const showClear = computed(() => {
    if (props.disabled || props.readonly || props.disableSearch) return false
    if (props.modelValue?.name?.trim()) return true
    const sv = selectedValue.value
    if (sv == null) return false
    if (typeof sv === "string") return sv.trim().length > 0
    return true
})

/**
 * Réserve l'espace à droite pour le loader ou le bouton effacer (évite le texte sous les icônes)
 */
const inputTrailingPaddingClass = computed(() =>
    isLoading.value || showClear.value ? "pr-10" : ""
)

// ================================
// WATCHERS
// ================================

/**
 * Synchronise la valeur sélectionnée avec le modelValue
 */
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue?.name) {
            // Mode manuel : synchronise la valeur du champ texte
            manualValue.value = newValue.name

            // Mode autocomplete : crée un objet Suggestion compatible
            selectedValue.value = {
                value: `${newValue.name}-${newValue.idcompany || newValue._id || "manual"}`,
                name: newValue.name,
                company: newValue,
            }
        } else {
            selectedValue.value = null
            manualValue.value = ""
        }
    },
    { immediate: true }
)

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Recherche des entreprises via l'API
 * @param event - Événement de complétion automatique
 */
async function onSearch(event: AutoCompleteCompleteEvent) {
    const query = event.query

    // Si la recherche est désactivée, on met à jour directement le modelValue
    if (props.disableSearch) {
        emit("update:modelValue", {
            ...props.modelValue,
            name: query,
        })
        suggestions.value = []
        return
    }

    if (!query || query.length < 2) {
        suggestions.value = []
        return
    }

    // Annuler la requête précédente
    controller.value?.abort()
    controller.value = new AbortController()

    isLoading.value = true

    const ctrl = controller.value

    /**
     * Recherche invités Homeys (échec isolé : n'empêche pas l'INSEE)
     */
    async function loadHomeysResults(): Promise<CompanySearchResult[]> {
        if (!services?.$searchCompany) return []
        try {
            return await services.$searchCompany({
                type: props.companyType,
                text: query,
                controller: ctrl,
            })
        } catch (error: unknown) {
            const err = error as Error
            if (err.name === "AbortError") throw err
            console.error(
                "Erreur lors de la recherche d'entreprise (Homeys):",
                error
            )
            return []
        }
    }

    /**
     * Recherche INSEE (échec isolé : n'empêche pas Homeys)
     */
    async function loadInseeResults(): Promise<CompanyInseeResult[]> {
        if (!services?.$searchCompanyInsee) return []
        try {
            return await services.$searchCompanyInsee(query, ctrl)
        } catch (error: unknown) {
            const err = error as Error
            if (err.name === "AbortError") throw err
            console.error(
                "Erreur lors de la recherche d'entreprise (INSEE):",
                error
            )
            return []
        }
    }

    try {
        const groups: SuggestionGroup[] = []
        const homeysItems: Suggestion[] = []
        const inseeItems: Suggestion[] = []

        const [homeyResults, inseeResults] = await Promise.all([
            loadHomeysResults(),
            loadInseeResults(),
        ])

        homeyResults.forEach((c: CompanySearchResult) => {
            homeysItems.push({
                value: `${c.name}-${c.idcompany || c._id}-homeys`,
                name: c.name,
                company: {
                    _id: c._id,
                    name: c.name,
                    type: c.type,
                    address: c.address,
                    idcompany: c.idcompany,
                    legalForm: c.legalForm,
                },
            })
        })

        inseeResults.forEach((c: CompanyInseeResult) => {
            const exists = homeysItems.some(
                (s) => s.company.idcompany === c.siren
            )
            if (!exists) {
                inseeItems.push({
                    value: `${c.name}-${c.siret}-INSEE`,
                    name: c.name,
                    company: {
                        name: c.name,
                        type: "entreprise",
                        address: c.address,
                        idcompany: c.siren,
                        legalForm:
                            c.forme_juridique_legale === "INCONNU"
                                ? undefined
                                : c.forme_juridique_legale,
                    },
                })
            }
        })

        // Construire les groupes (seulement si des items existent)
        if (homeysItems.length > 0) {
            groups.push({
                label: "Dans mes invités",
                items: homeysItems,
            })
        }

        if (inseeItems.length > 0) {
            groups.push({
                label: "INSEE",
                items: inseeItems,
            })
        }

        suggestions.value = groups
    } catch (error: any) {
        if (error.name !== "AbortError") {
            console.error("Erreur lors de la recherche d'entreprise:", error)
        }
        suggestions.value = []
    } finally {
        isLoading.value = false
    }
}

// ================================
// INTERACTIONS
// ================================

/**
 * Sélectionne une entreprise dans les suggestions
 * @param suggestion - Suggestion sélectionnée
 */
function onSelectCompany(suggestion: Suggestion) {
    emit("update:modelValue", suggestion.company)
}

/**
 * Efface la valeur
 */
function onClear() {
    selectedValue.value = null
    manualValue.value = ""
    suggestions.value = []
    emit("update:modelValue", null)
}

/**
 * Gère la saisie manuelle du nom de l'entreprise
 * @param value - Valeur saisie
 */
function onManualInput(value: string) {
    // Évite le double emit si la valeur n'a pas changé
    if (manualValue.value === value) return

    manualValue.value = value
    emit("update:modelValue", {
        ...props.modelValue,
        name: value,
    })
}
</script>

<template>
    <BaseInputWrapper
        :input-id="inputId"
        :label="label"
        :hint="hint"
        :error="error"
        :required="required"
    >
        <!-- ================================ -->
        <!-- INPUT TEXT (mode manuel) -->
        <!-- ================================ -->
        <BaseInputText
            v-if="disableSearch"
            :id="inputId"
            :model-value="manualValue"
            :disabled="disabled"
            :readonly="readonly"
            :invalid="!!error"
            :show-clear="true"
            fluid
            @update:model-value="onManualInput"
        />

        <!-- ================================ -->
        <!-- AUTOCOMPLETE PRIMEVUE (mode recherche) -->
        <!-- ================================ -->
        <div v-else class="relative w-full">
            <BaseAutoComplete
                v-model="selectedValue"
                :input-id="inputId"
                :suggestions="suggestions"
                :disabled="disabled"
                :loading="isLoading"
                option-label="name"
                option-group-label="label"
                option-group-children="items"
                :min-length="2"
                :delay="500"
                fluid
                :invalid="!!error"
                :pt="{
                    root: 'w-full!',
                    loader: {
                        class: 'z-[2]',
                    },
                    pcInputText: {
                        root: {
                            readonly: readonly,
                            class: inputTrailingPaddingClass,
                        },
                    },
                }"
                @complete="onSearch"
                @option-select="onSelectCompany($event.value)"
                @clear="onClear"
            >
                <!-- ================================ -->
                <!-- TEMPLATE DU GROUPE -->
                <!-- ================================ -->
                <template #optiongroup="{ option }">
                    <div class="font-medium text-gray-700">
                        {{ option.label }}
                    </div>
                </template>

                <!-- ================================ -->
                <!-- TEMPLATE DES OPTIONS -->
                <!-- ================================ -->
                <template #option="{ option }">
                    <ItemCompany :company="option.company" />
                </template>

                <!-- ================================ -->
                <!-- TEMPLATE VIDE -->
                <!-- ================================ -->
                <template #empty>
                    <div class="px-3 py-2 text-sm text-gray-500">
                        Aucune entreprise trouvée
                    </div>
                </template>
            </BaseAutoComplete>
            <button
                v-if="showClear && !isLoading"
                type="button"
                class="absolute end-2 top-1/2 z-[3] -translate-y-1/2 rounded p-0.5 text-gray-500 hover:text-gray-800 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500 cursor-pointer"
                aria-label="Effacer la saisie"
                @click="onClear"
            >
                <span class="pi pi-times text-sm" aria-hidden="true" />
            </button>
        </div>
    </BaseInputWrapper>
</template>
